/* eslint-disable no-nested-ternary */
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  Alert, Snackbar,
} from '@mui/material';
import { capitalCase } from 'change-case';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  ResetContentList,
  selectContentList,
  selectContentPageNumber,
  selectGetContentListState,
  selectHasMoreContent,
  UpdateHasMoreContent,
  UpdatePageNumber,
} from '../../../store/slices/contentSlice';
import { getContent } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { getRandomInteger } from '../../../common/RandomNumberHelper';
import { HomeFeedPersonalizeAlgorithmFlag } from '../../../common/OptimizelyFlagsConstants';
import { useOptimizelyFlag } from '../../../common/hooks/useOptimizelyFlag';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { useScrollRestoration } from '../../../common/hooks/useScrollRestoration';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { Content, ContentFeedSection, FeedInteractionAction } from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';
import { Logger } from '../../../config/logging/logger';

interface RecommendedContentCategoryProps {
  categoryId: string;
  keyProp: string;
  viewMorePage?: boolean
  setSomeContentLoadedInitially: (value: boolean) => void
  contentFeedSection: ContentFeedSection
}

export function RecommendedContentCategory({
  categoryId,
  keyProp,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:RecommendedContentCategoryProps) {
  const storeContentList = useAppSelector(selectContentList); // using the generic content list
  const contentList = useMemo(() => storeContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeContentList, contentFeedSection]);

  const getContentListState = useAppSelector(selectGetContentListState);
  const contentFetchInitiated = useRef(false);
  const { trackFeedInteraction } = useCustomEventTracking();
  const storeHasMoreContent = useAppSelector(selectHasMoreContent);
  const [hasMoreContent, setHasMoreContent] = useState(storeHasMoreContent);
  // the current value of page is from store
  const page = useAppSelector(selectContentPageNumber);
  const [algorithm, setAlgorithm] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  useTrackCategoryContentVisibility({
    categoryContentList: contentList,
  });
  const isMobile = useIsMobile();
  const moreContentPageSize = isMobile ? 5 : 15;
  const pageSize = viewMorePage ? moreContentPageSize : 3;
  const logger = Logger.create('RecommendedContentCategory');
  const {
    decision: homeFeedPersonalizeDecision,
    isDecisionReady,
  } = useOptimizelyFlag({
    flag: HomeFeedPersonalizeAlgorithmFlag,
  });

  useEffect(() => {
    setHasMoreContent(storeHasMoreContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeHasMoreContent]);

  /**
   * Use a new random integer for each page refresh,
   * this will make sure each time different content will be fetched from the backend.
   * When user scrolls down, the same random seed will be used to make sure content
   * is consistent across different pages.
   */
  const [randomContentSeed] = useState(() => getRandomInteger(1, 10000));
  const isReady = useMemo(() => isDecisionReady, [isDecisionReady]);
  const { restoreScroll } = useScrollRestoration();

  const isLoading = useMemo(
    () => (getContentListState === 'pending' && !initialFetchDone),
    [getContentListState, initialFetchDone],
  );

  useEffect(() => {
    const algorithmFromParams = searchParams.get('algorithm');
    setAlgorithm(algorithmFromParams || undefined);
  }, [searchParams]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (restoreScroll()) {
      return;
    }

    if (!contentFetchInitiated.current) {
      contentFetchInitiated.current = true;
      dispatch(ResetContentList([])); // initial content list state
      dispatch(getContent({
        pageSize,
        page: 1,
        randomContentSeed,
        homeFeedPersonalizeDecision,
      }))
        .then(unwrapResult)
        .then((res) => {
          setOpenErrorSnackbar(false);
          setSomeContentLoadedInitially(true);
          setInitialFetchDone(true);
          // Move to page to 2 so infinite scroll can begin from there
          dispatch(UpdatePageNumber(2));
          if (res.length === pageSize) {
            dispatch(UpdateHasMoreContent(true));
          } else {
            dispatch(UpdateHasMoreContent(false));
          }
        })
        .catch((err) => {
          setOpenErrorSnackbar(true);
          logger.error(Logger.format(err));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchMoreRecommendedContent = () => {
    if (page > 1) {
      trackFeedInteraction(
        'Load',
        'MoreContent',
        {
          action: FeedInteractionAction.ReachEnd,
        },
      );
    }

    dispatch(getContent({
      pageSize: moreContentPageSize,
      page,
      randomContentSeed,
      homeFeedPersonalizeDecision,
      algorithm,
    }))
      .then(unwrapResult)
      .then((res) => {
        setOpenErrorSnackbar(false);
        // our currrent BE ensures that we never get more content in the response than pageSize
        dispatch(UpdateHasMoreContent(res.length === moreContentPageSize));
        if (hasMoreContent) {
          dispatch(UpdatePageNumber(page + 1));
        }
      }).catch((e) => {
        setOpenErrorSnackbar(true);
        logger.error(Logger.format(e));
      });
  };

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {(isLoading || !isReady) ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${keyProp}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${keyProp}-content`}
          categoryId={categoryId}
          storeContentList={contentList}
          onNextPage={fetchMoreRecommendedContent}
          hasMoreContent={hasMoreContent}
          showSearchBar={true}
        />
      ) : contentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${keyProp}-content`}
          categoryTitle={capitalCase(keyProp)}
          storeContentList={contentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
